import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import api from "@/api";

/**
 * IntelLibrary - Browse all intel with filters
 */
const IntelLibrary = ({ accountId, onIntelClick }) => {
  const [intel, setIntel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tickerFilter, setTickerFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);

  // Fetch intel
  const fetchIntel = async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      const params = {};

      if (tickerFilter) {
        params.ticker = tickerFilter.toUpperCase();
      }

      const response = await api.get(`/intel`, { params });
      setIntel(response.data.items || response.data || []);
    } catch (error) {
      console.error("Error fetching intel:", error);
      setIntel([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntel();
  }, [accountId, tickerFilter]);

  // Client-side filtering for text and date (since backend doesn't support these filters yet)
  const filteredIntel = intel.filter((item) => {
    // Text filter (search in thesis, author, platform)
    if (textFilter) {
      const searchText = textFilter.toLowerCase();
      const matchesThesis = item.thesis?.toLowerCase().includes(searchText);
      const matchesAuthor = item.author?.toLowerCase().includes(searchText);
      const matchesPlatform = item.platform?.toLowerCase().includes(searchText);
      const matchesTags = item.tags?.some((tag) => tag.toLowerCase().includes(searchText));

      if (!matchesThesis && !matchesAuthor && !matchesPlatform && !matchesTags) {
        return false;
      }
    }

    // Date filter
    if (startDateFilter) {
      const itemDate = new Date(item.timestamp);
      const startDate = new Date(startDateFilter);
      if (itemDate < startDate) return false;
    }

    if (endDateFilter) {
      const itemDate = new Date(item.timestamp);
      const endDate = new Date(endDateFilter);
      endDate.setHours(23, 59, 59, 999); // Include entire end day
      if (itemDate > endDate) return false;
    }

    return true;
  });

  // Sort by timestamp (newest first)
  const sortedIntel = [...filteredIntel].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const handleClearFilters = () => {
    setTickerFilter("");
    setTextFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const handleDeleteIntel = async (intelId, e) => {
    // Stop event propagation to prevent clicking the card
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this intel item?")) {
      return;
    }

    try {
      setDeletingId(intelId);
      await api.delete(`/intel/${intelId}`);

      // Remove from local state
      setIntel((prev) => prev.filter((item) => item.id !== intelId));
    } catch (error) {
      console.error("Error deleting intel:", error);
      alert("Failed to delete intel");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm("This will remove duplicate intel based on filename, keeping only the oldest copy of each. Continue?")) {
      return;
    }

    try {
      setCleaningUp(true);
      const response = await api.post(`/intel/cleanup-duplicates`);

      alert(response.data.message);

      // Refresh intel list
      await fetchIntel();
    } catch (error) {
      console.error("Error cleaning up duplicates:", error);
      alert("Failed to cleanup duplicates");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="section-content">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <h3 className="section-title">Intel Library</h3>
            <button
              onClick={handleCleanupDuplicates}
              disabled={cleaningUp || loading}
              className="btn btn-secondary"
              style={{
                padding: "0.5rem 0.75rem",
                fontSize: "0.875rem",
                opacity: cleaningUp || loading ? 0.6 : 1,
                cursor: cleaningUp || loading ? "not-allowed" : "pointer",
              }}
              title="Remove duplicate intel based on filename"
            >
              {cleaningUp ? "Cleaning..." : "🧹 Cleanup Duplicates"}
            </button>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            {/* Ticker Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Ticker:
              </label>
              <input
                type="text"
                value={tickerFilter}
                onChange={(e) => setTickerFilter(e.target.value.toUpperCase())}
                placeholder="e.g. AAPL"
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                  width: "100px",
                }}
              />
            </div>

            {/* Text Search Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Search:
              </label>
              <input
                type="text"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="thesis, author, tags..."
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                  width: "200px",
                }}
              />
            </div>

            {/* Date Range Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                From:
              </label>
              <input
                type="date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                To:
              </label>
              <input
                type="date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
              {(tickerFilter || textFilter || startDateFilter || endDateFilter) && (
                <button
                  onClick={handleClearFilters}
                  className="btn btn-secondary"
                  style={{
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.875rem",
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div
          className="text-muted"
          style={{
            padding: "2rem",
            textAlign: "center",
          }}
        >
          Loading intel...
        </div>
      ) : sortedIntel.length === 0 ? (
        /* Empty State */
        <div
          className="text-muted"
          style={{
            padding: "2rem",
            textAlign: "center",
          }}
        >
          {intel.length === 0 ? "No intel found. Drop a screenshot to get started!" : "No intel matches your filters"}
        </div>
      ) : (
        /* Grid View */
        <div style={{ padding: "1.5rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {sortedIntel.map((item, index) => (
              <div
                key={item.id || index}
                style={{
                  background: "var(--card-bg)",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  transition: "all 0.2s",
                  cursor: item.tickers && item.tickers.length > 0 ? "pointer" : "default",
                  position: "relative",
                }}
                onClick={() => {
                  if (onIntelClick && item.tickers && item.tickers.length > 0) {
                    onIntelClick(item.tickers[0]); // Open sidebar for first ticker
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Delete button */}
                <button
                  onClick={(e) => handleDeleteIntel(item.id, e)}
                  disabled={deletingId === item.id}
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    background: deletingId === item.id ? "#cbd5e0" : "rgba(254, 215, 215, 0.95)",
                    border: "1px solid #fc8181",
                    borderRadius: "4px",
                    color: "#c53030",
                    fontSize: "0.75rem",
                    padding: "0.25rem 0.5rem",
                    cursor: deletingId === item.id ? "not-allowed" : "pointer",
                    fontWeight: "600",
                    zIndex: 10,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (deletingId !== item.id) {
                      e.currentTarget.style.background = "rgba(252, 129, 129, 0.95)";
                      e.currentTarget.style.color = "white";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (deletingId !== item.id) {
                      e.currentTarget.style.background = "rgba(254, 215, 215, 0.95)";
                      e.currentTarget.style.color = "#c53030";
                    }
                  }}
                  title="Delete this intel"
                >
                  {deletingId === item.id ? "..." : "×"}
                </button>

                {/* Screenshot */}
                {item.screenshot_data && (
                  <div
                    style={{
                      width: "100%",
                      height: "200px",
                      overflow: "hidden",
                      background: "#f7fafc",
                    }}
                  >
                    <img
                      src={item.screenshot_data}
                      alt="Intel screenshot"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div style={{ padding: "1rem" }}>
                  {/* Metadata */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                    {item.tickers && item.tickers.map((ticker) => (
                      <Link
                        key={ticker}
                        to={`/ticker/${ticker}?accountId=${accountId}`}
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click event
                      >
                        <span
                          style={{
                            background: "#4299e1",
                            color: "white",
                            padding: "0.125rem 0.5rem",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          {ticker}
                        </span>
                      </Link>
                    ))}
                    {item.platform && (
                      <span style={{ fontSize: "0.75rem", color: "#718096" }}>
                        {item.platform}
                      </span>
                    )}
                  </div>

                  {/* Author */}
                  {item.author && (
                    <div style={{ fontSize: "0.875rem", color: "#4a5568", fontWeight: "600", marginBottom: "0.5rem" }}>
                      {item.author}
                    </div>
                  )}

                  {/* Thesis */}
                  {item.thesis && (
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#2d3748",
                        marginBottom: "0.75rem",
                        lineHeight: "1.5",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.thesis}
                    </div>
                  )}

                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            background: "#e2e8f0",
                            color: "#4a5568",
                            padding: "0.125rem 0.375rem",
                            borderRadius: "4px",
                            fontSize: "0.625rem",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div style={{ fontSize: "0.7rem", color: "#a0aec0" }}>
                    {formatDate(item.timestamp, "MMM d, yyyy h:mm a")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelLibrary;
